import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  Chip,
  LinearProgress,
  Snackbar,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Visibility,
  VisibilityOff,
  LogoutOutlined,
  SaveOutlined,
  UndoRounded,
  PersonOutline,
  MailOutline,
  PhoneOutlined,
  DnsOutlined,
  LockOutlined,
  LockResetOutlined,
  CalendarMonthOutlined,
  VerifiedUserOutlined,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { useNavigate } from "react-router-dom";

import CustomAppBar from "../../../components/CustomAppBar";
import { customerService } from "../services/customerService";
import { parseServerDate } from "../../../utils/dateUtils";
import {
  bgGradient,
  textPrimary,
  textSecondary,
  primaryDark,
  accentColor,
  buttonGradient,
  buttonGradientHover,
  buttonShadow,
  buttonShadowHover,
  cardShadow,
  ROUTES,
  MESSAGES,
  STORAGE_KEYS,
} from "../../../constants/consts";

// --- Animasyonlar ---
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// --- Yardımcılar ---
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_RE = /^[0-9+()\-\s]{7,20}$/;
const MIN_PASSWORD = 6;

const TYPE_LABELS = {
  user: "Kullanıcı",
  rootuser: "Yetkili kullanıcı",
  destek: "Destek personeli",
  admin: "Yönetici",
};

const STRENGTH = [
  { label: "Çok zayıf", color: "#DC2626" },
  { label: "Zayıf", color: "#EA580C" },
  { label: "Orta", color: "#CA8A04" },
  { label: "İyi", color: "#65A30D" },
  { label: "Güçlü", color: "#16A34A" },
];

function passwordScore(pw) {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}

function formatDay(value) {
  const d = parseServerDate(value);
  if (isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function initialsOf(name = "") {
  const t = String(name).trim();
  return t ? t.slice(0, 2).toLocaleUpperCase("tr-TR") : "?";
}

// Oturum bilgisi localStorage/sessionStorage'da tutuluyor; kullanıcı adı değişince eşitle
function syncStoredUsername(name) {
  [localStorage, sessionStorage].forEach((st) => {
    try {
      if (st.getItem(STORAGE_KEYS.USERNAME)) {
        st.setItem(STORAGE_KEYS.USERNAME, name);
      }
      const raw = st.getItem(STORAGE_KEYS.USER);
      if (raw) {
        const u = JSON.parse(raw);
        u.username = name;
        st.setItem(STORAGE_KEYS.USER, JSON.stringify(u));
      }
    } catch {
      /* depolama erişilemezse sessizce geç */
    }
  });
}

const cardSx = {
  borderRadius: 4,
  boxShadow: cardShadow,
  backgroundColor: "rgba(255,255,255,0.98)",
};

function Section({ icon, title, subtitle, delay = 0, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        ...cardSx,
        p: { xs: 2.5, sm: 3 },
        animation: `${fadeInUp} 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s backwards`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: primaryDark,
            backgroundColor: "rgba(4,87,129,0.09)",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography fontWeight={800} color={textPrimary} lineHeight={1.2}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: textSecondary }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
      {children}
    </Paper>
  );
}

export default function CustomerSettings({ user, onLogout }) {
  const navigate = useNavigate();

  const [form, setForm] = useState({ username: "", email: "", phone: "" });
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [touched, setTouched] = useState({});

  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [logoutDialog, setLogoutDialog] = useState(false);

  const userId =
    user?.user_id ||
    JSON.parse(
      localStorage.getItem(STORAGE_KEYS.USER) ||
        sessionStorage.getItem(STORAGE_KEYS.USER) ||
        "{}",
    )?.user_id;

  const loadUser = async ({ silent = false } = {}) => {
    if (!userId) {
      setError(MESSAGES.SETTINGS_LOAD_ERROR);
      setLoading(false);
      return;
    }
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const data = await customerService.getUserById(userId);
      const u = Array.isArray(data) ? data[0] : data;
      if (!u) throw new Error("Kullanıcı bulunamadı");

      setOriginal(u);
      setForm({
        username: u.username || "",
        email: u.email || "",
        phone: u.phone || "",
      });
      setPassword("");
      setPassword2("");
      setTouched({});
    } catch (err) {
      console.error(err);
      setError(MESSAGES.SETTINGS_LOAD_ERROR);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line
  }, []);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };
  const markTouched = (field) => () =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  // --- Doğrulama ---
  const errors = useMemo(() => {
    const e = {};
    if (!form.username.trim()) e.username = "Kullanıcı adı boş olamaz.";
    if (form.email.trim() && !EMAIL_RE.test(form.email.trim()))
      e.email = "Geçerli bir e-posta adresi girin.";
    if (form.phone.trim() && !PHONE_RE.test(form.phone.trim()))
      e.phone = "Geçerli bir telefon numarası girin.";
    if (password && password.length < MIN_PASSWORD)
      e.password = `Şifre en az ${MIN_PASSWORD} karakter olmalı.`;
    if (password && password2 !== password)
      e.password2 = "Şifreler eşleşmiyor.";
    return e;
  }, [form, password, password2]);

  const hasError = Object.keys(errors).length > 0;
  const showErr = (f) => (touched[f] && errors[f]) || "";

  const dirty = useMemo(() => {
    if (!original) return false;
    return (
      form.username.trim() !== (original.username || "") ||
      form.email.trim() !== (original.email || "") ||
      form.phone.trim() !== (original.phone || "") ||
      password.length > 0
    );
  }, [form, password, original]);

  const canSave = dirty && !hasError && !saving;

  const score = passwordScore(password);

  const handleReset = () => {
    if (!original) return;
    setForm({
      username: original.username || "",
      email: original.email || "",
      phone: original.phone || "",
    });
    setPassword("");
    setPassword2("");
    setTouched({});
    setError("");
  };

  const handleSave = async () => {
    // Hatalı alanlar hemen görünsün
    setTouched({
      username: true,
      email: true,
      phone: true,
      password: true,
      password2: true,
    });
    if (hasError) return;

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const newName = form.username.trim();
      const passwordChanged = password.length > 0;

      await customerService.updateUserData({
        id: userId,
        username: newName,
        phone: form.phone.trim(),
        email: form.email.trim(),
        // Sunucu kodu ve hesap türü müşteri tarafından değiştirilemez
        sunucu_kodu: original?.sunucu_kodu || "",
        type: original?.type || "user",
        // Boş = şifre değişmez. (Eskiden mevcut hash geri gönderiliyor ve
        // sunucuda tekrar hash'lenerek şifre bozuluyordu.)
        pass: passwordChanged ? password : "",
      });

      syncStoredUsername(newName);
      setSuccess(
        passwordChanged
          ? "Bilgileriniz ve şifreniz güncellendi."
          : MESSAGES.SETTINGS_UPDATE_SUCCESS,
      );
      await loadUser({ silent: true });
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          MESSAGES.SETTINGS_UPDATE_ERROR,
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmLogout = () => {
    setLogoutDialog(false);
    if (onLogout) onLogout();
    else navigate(ROUTES.LOGIN, { replace: true });
  };

  const typeLabel =
    TYPE_LABELS[String(original?.type || "").toLowerCase()] || "Kullanıcı";
  const memberSince = formatDay(original?.created_at);

  return (
    <Box
      sx={{
        height: "100dvh",
        background: bgGradient,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        "&::before": {
          content: '""',
          position: "absolute",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.10)",
          top: -100,
          right: -100,
          animation: `${fadeIn} 1.2s ease-out both`,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          bottom: -80,
          left: -80,
          animation: `${fadeIn} 1.4s ease-out 0.1s both`,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <CustomAppBar
          title={MESSAGES.SETTINGS_APPBAR}
          isNotHome
          onLogout={onLogout}
          extraActions={
            <IconButton
              onClick={() => loadUser()}
              title={MESSAGES.SETTINGS_REFRESH}
              disabled={loading || saving}
              sx={{
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.12)",
                backdropFilter: "blur(8px)",
                mr: 1,
                transition: "transform 0.2s ease, background-color 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.22)",
                  transform: "scale(1.08)",
                },
                "&:active": { transform: "scale(0.92)" },
              }}
            >
              <RefreshIcon />
            </IconButton>
          }
        />

        <Container
          maxWidth="sm"
          sx={{
            pt: { xs: "88px", sm: "104px" },
            pb: 4,
            flex: "1 0 auto",
          }}
        >
          {loading ? (
            <Paper
              elevation={0}
              sx={{
                ...cardSx,
                display: "flex",
                justifyContent: "center",
                py: 8,
              }}
            >
              <CircularProgress />
            </Paper>
          ) : (
            <Stack spacing={2.5}>
              {error && (
                <Alert
                  severity="error"
                  onClose={() => setError("")}
                  sx={{
                    borderRadius: 2,
                    animation: `${fadeInUp} 0.35s ease-out both`,
                  }}
                >
                  {error}
                </Alert>
              )}

              {/* ───────── Profil özeti ───────── */}
              {original && (
                <Paper
                  elevation={0}
                  sx={{
                    ...cardSx,
                    p: { xs: 2.5, sm: 3 },
                    animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.05s backwards`,
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        fontWeight: 800,
                        fontSize: 22,
                        background: `linear-gradient(135deg, ${primaryDark} 0%, ${accentColor} 100%)`,
                        boxShadow: "0 6px 16px rgba(4,87,129,0.30)",
                      }}
                    >
                      {initialsOf(original.username)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="h6"
                        fontWeight={800}
                        color={textPrimary}
                        noWrap
                        title={original.username}
                      >
                        {original.username}
                      </Typography>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ color: textSecondary }}
                        title={original.email}
                      >
                        {original.email || "E-posta eklenmemiş"}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ mt: 2 }}
                  >
                    <Chip
                      size="small"
                      icon={<VerifiedUserOutlined />}
                      label={typeLabel}
                      sx={{ fontWeight: 700 }}
                    />
                    {original.sunucu_kodu && (
                      <Chip
                        size="small"
                        icon={<DnsOutlined />}
                        label={original.sunucu_kodu}
                        sx={{ fontWeight: 700 }}
                      />
                    )}
                    {memberSince && (
                      <Chip
                        size="small"
                        icon={<CalendarMonthOutlined />}
                        label={`Üyelik: ${memberSince}`}
                        sx={{ fontWeight: 700 }}
                      />
                    )}
                  </Stack>
                </Paper>
              )}

              {/* ───────── Hesap bilgileri ───────── */}
              <Section
                icon={<PersonOutline />}
                title="Hesap bilgileri"
                subtitle="Giriş ve iletişim bilgileriniz"
                delay={0.1}
              >
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    required
                    label={MESSAGES.SETTINGS_USERNAME}
                    value={form.username}
                    onChange={handleChange("username")}
                    onBlur={markTouched("username")}
                    disabled={saving}
                    autoComplete="username"
                    error={!!showErr("username")}
                    helperText={showErr("username")}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutline fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label={MESSAGES.SETTINGS_EMAIL}
                    type="email"
                    value={form.email}
                    onChange={handleChange("email")}
                    onBlur={markTouched("email")}
                    disabled={saving}
                    autoComplete="email"
                    error={!!showErr("email")}
                    helperText={
                      showErr("email") ||
                      "Şifremi unuttum e-postaları bu adrese gönderilir."
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MailOutline fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label={MESSAGES.SETTINGS_PHONE}
                    value={form.phone}
                    onChange={handleChange("phone")}
                    onBlur={markTouched("phone")}
                    disabled={saving}
                    autoComplete="tel"
                    inputMode="tel"
                    error={!!showErr("phone")}
                    helperText={showErr("phone")}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneOutlined fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              </Section>

              {/* ───────── Sunucu ───────── */}
              <Section
                icon={<DnsOutlined />}
                title="Sunucu"
                subtitle="Hesabınızın bağlı olduğu sunucu"
                delay={0.15}
              >
                <TextField
                  fullWidth
                  label={MESSAGES.SETTINGS_SUNUCU_KODU}
                  value={original?.sunucu_kodu || ""}
                  disabled
                  helperText="Sunucu kodunu değiştirmek için Nortek ile iletişime geçin."
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Section>

              {/* ───────── Güvenlik ───────── */}
              <Section
                icon={<LockResetOutlined />}
                title="Şifre"
                subtitle="Boş bırakırsanız şifreniz değişmez"
                delay={0.2}
              >
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Yeni şifre"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={markTouched("password")}
                    disabled={saving}
                    autoComplete="new-password"
                    error={!!showErr("password")}
                    helperText={showErr("password")}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((v) => !v)}
                            edge="end"
                            disabled={saving}
                            aria-label={
                              showPassword ? "Şifreyi gizle" : "Şifreyi göster"
                            }
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {password && (
                    <Box>
                      <LinearProgress
                        variant="determinate"
                        value={(score / 4) * 100}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: "rgba(15,50,65,0.08)",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: STRENGTH[score].color,
                            borderRadius: 3,
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: STRENGTH[score].color, fontWeight: 700 }}
                      >
                        Şifre gücü: {STRENGTH[score].label}
                      </Typography>
                    </Box>
                  )}

                  <TextField
                    fullWidth
                    label="Yeni şifre (tekrar)"
                    type={showPassword ? "text" : "password"}
                    value={password2}
                    onChange={(e) => setPassword2(e.target.value)}
                    onBlur={markTouched("password2")}
                    disabled={saving || !password}
                    autoComplete="new-password"
                    error={!!showErr("password2")}
                    helperText={
                      showErr("password2") ||
                      `En az ${MIN_PASSWORD} karakter; harf, rakam ve sembol karışımı önerilir.`
                    }
                  />
                </Stack>
              </Section>

              {/* ───────── Oturum ───────── */}
              <Paper
                elevation={0}
                sx={{
                  ...cardSx,
                  p: { xs: 2.5, sm: 3 },
                  animation: `${fadeInUp} 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.25s backwards`,
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography fontWeight={800} color={textPrimary}>
                      Oturum
                    </Typography>
                    <Typography variant="caption" sx={{ color: textSecondary }}>
                      Bu cihazdaki oturumunuzu sonlandırın.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<LogoutOutlined />}
                    onClick={() => setLogoutDialog(true)}
                    disabled={saving}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                      px: 3,
                    }}
                  >
                    {MESSAGES.SETTINGS_LOGOUT}
                  </Button>
                </Stack>
              </Paper>

              {/* ───────── Kaydet çubuğu (sayfa kayarken altta kalır) ───────── */}
              <Box
                sx={{
                  position: "sticky",
                  bottom: 12,
                  zIndex: 2,
                  p: 1.5,
                  borderRadius: 3,
                  backgroundColor: "rgba(255,255,255,0.97)",
                  backdropFilter: "blur(8px)",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    minWidth: 140,
                    fontWeight: 600,
                    color: dirty ? "#B45309" : textSecondary,
                  }}
                >
                  {dirty
                    ? hasError
                      ? "Lütfen işaretli alanları düzeltin."
                      : "Kaydedilmemiş değişiklikler var."
                    : "Tüm bilgiler güncel."}
                </Typography>
                <Button
                  onClick={handleReset}
                  disabled={!dirty || saving}
                  startIcon={<UndoRounded />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                  }}
                >
                  Vazgeç
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={!canSave}
                  startIcon={saving ? null : <SaveOutlined />}
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    background: buttonGradient,
                    boxShadow: buttonShadow,
                    transition:
                      "transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease",
                    "&:hover": {
                      background: buttonGradientHover,
                      boxShadow: buttonShadowHover,
                      transform: "translateY(-1px)",
                    },
                    "&:active": { transform: "translateY(0) scale(0.99)" },
                    "&.Mui-disabled": { background: "#c7c7c7", color: "#fff" },
                  }}
                >
                  {saving ? (
                    <CircularProgress size={22} sx={{ color: "#fff" }} />
                  ) : (
                    "Kaydet"
                  )}
                </Button>
              </Box>
            </Stack>
          )}
        </Container>
      </Box>

      {/* Başarı bildirimi */}
      <Snackbar
        open={!!success}
        autoHideDuration={3500}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccess("")}
          sx={{ borderRadius: 2 }}
        >
          {success}
        </Alert>
      </Snackbar>

      {/* Çıkış onay dialog'u */}
      <Dialog open={logoutDialog} onClose={() => setLogoutDialog(false)}>
        <DialogTitle>Çıkış Yap</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Hesabınızdan çıkmak istediğinize emin misiniz?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialog(false)}>İptal</Button>
          <Button color="error" variant="contained" onClick={confirmLogout}>
            Çıkış Yap
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
